import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Calendar, DollarSign, TrendingUp, Maximize2, RefreshCw, FileText, User, Building2 } from 'lucide-react';
import { getPledgeById, getLoanById, getPledgesByLoan, updateLoanCollateralStatus, updateLoanStatus, getPledgeInterest, payPledgeInterest } from '../services/api';
import Toast from '../components/Toast';
import { useLanguage } from '../contexts/LanguageContext';

const PledgeDetails = () => {
  const { pledgeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const texts = t('pledgeDetails');
  const loanTexts = t('loanDetails');
  const collateralListTexts = t('collateral');
  const naLabel = t('loans.labels.na');
  const [pledge, setPledge] = useState(null);
  const [loan, setLoan] = useState(null);
  const [interestSummary, setInterestSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showImageModal, setShowImageModal] = useState(false);
  
  // Use refs to track if API calls are in progress and prevent duplicates
  const loadingRef = useRef(false);
  const loanLoadingRef = useRef(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusModalLoading, setStatusModalLoading] = useState(false);
  const [interestPayModalVisible, setInterestPayModalVisible] = useState(false);
  const [interestPayLoading, setInterestPayLoading] = useState(false);
  const [loanPledges, setLoanPledges] = useState([]);

  const loadLoanDetails = useCallback(async (loanId) => {
    if (loanLoadingRef.current || !loanId) return;

    loanLoadingRef.current = true;
    try {
      const result = await getLoanById(loanId);
      if (result.success && result.data) {
        setLoan(result.data);
      } else if (result.message) {
        setToast({ show: true, message: result.message, type: 'error' });
      }
    } catch (error) {
      console.error('Error loading loan:', error);
      setToast({ show: true, message: loanTexts.messages.loadError, type: 'error' });
    } finally {
      loanLoadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInterestSummary = useCallback(async (businessId, id) => {
    if (!businessId || !id) return;
    try {
      const result = await getPledgeInterest(businessId, id);
      if (result.success && result.data) {
        setInterestSummary(result.data);
      }
    } catch (error) {
      console.error('Error loading pledge interest:', error);
    }
  }, []);

  const loadLoanPledgeHistory = useCallback(async (businessId, loanId) => {
    if (!businessId || !loanId) return;
    try {
      const result = await getPledgesByLoan(businessId, loanId);
      if (result.success && Array.isArray(result.data)) {
        const sorted = [...result.data].sort((a, b) => {
          const activeScore = (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0);
          if (activeScore !== 0) return activeScore;
          return String(b.pledgeDate || '').localeCompare(String(a.pledgeDate || ''));
        });
        setLoanPledges(sorted);
      }
    } catch (error) {
      console.error('Error loading pledge history:', error);
    }
  }, []);

  const loadPledgeDetails = useCallback(async () => {
    if (loadingRef.current) return;

    loadingRef.current = true;
    setLoading(true);
    try {
      if (location.state?.pledge) {
        setPledge(location.state.pledge);
        loadLoanDetails(location.state.pledge.loanId);
      }

      const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      const businessId = selectedBusiness?.id;

      if (!businessId) {
        setToast({ show: true, message: texts.messages.noBusiness, type: 'error' });
        return;
      }

      const result = await getPledgeById(businessId, pledgeId);

      if (result.success && result.data) {
        setPledge(result.data);
        loadLoanDetails(result.data.loanId);
        await loadInterestSummary(businessId, pledgeId);
        await loadLoanPledgeHistory(businessId, result.data.loanId);
      } else {
        setToast({ show: true, message: result.message || texts.notFound, type: 'error' });
        setTimeout(() => navigate('/collateral'), 2000);
      }
    } catch (error) {
      console.error('Error loading pledge:', error);
      setToast({ show: true, message: texts.messages.loadError, type: 'error' });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pledgeId, location.state, navigate, loadLoanDetails, loadInterestSummary, loadLoanPledgeHistory]);

  useEffect(() => {
    loadPledgeDetails();
  }, [loadPledgeDetails]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatCurrencyExact = (amount) => {
    const value = Number(amount || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return naLabel;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatShortDate = (dateString) => {
    if (!dateString) return naLabel;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'hi' ? 'hi-IN' : 'en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const handlePayInterestConfirm = async () => {
    if (!pledge || interestPayLoading) return;
    const selectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
    const businessId = selectedBusiness?.id;
    if (!businessId) {
      setToast({ show: true, message: texts.messages.noBusiness, type: 'error' });
      return;
    }

    setInterestPayLoading(true);
    try {
      const result = await payPledgeInterest(businessId, pledgeId);
      if (!result.success) {
        throw new Error(result.message || texts.messages.interestPayError);
      }
      setInterestSummary(result.data);
      setInterestPayModalVisible(false);
      setToast({ show: true, message: texts.messages.interestPaySuccess, type: 'success' });
    } catch (error) {
      console.error('Error paying pledge interest:', error);
      setToast({
        show: true,
        message: error.message || texts.messages.interestPayError,
        type: 'error'
      });
    } finally {
      setInterestPayLoading(false);
    }
  };

  const getStatusColors = (status) => {
    switch (status) {
      case 'IN_HAND':
        return { backgroundColor: '#dcfce7', color: '#166534' };
      case 'PLEDGED':
        return { backgroundColor: '#fef3c7', color: '#92400e' };
      case 'AWAITING':
        return { backgroundColor: '#fee2e2', color: '#991b1b' };
      case 'HANDOVER':
        return { backgroundColor: '#e0e7ff', color: '#3730a3' };
      case 'COMPLETED':
        return { backgroundColor: '#e0e7ff', color: '#3730a3' };
      case 'CLOSED':
        return { backgroundColor: '#d1fae5', color: '#166534' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#6b7280' };
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: '1rem', color: 'var(--text-secondary)' }} />
          <p style={{ fontSize: '1.125rem', color: 'var(--text-secondary)' }}>{texts.loading}</p>
        </div>
      </div>
    );
  }

  if (!pledge) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-secondary)' }}>{texts.notFound}</p>
          <button 
            onClick={() => navigate('/collateral')}
            className="btn btn-primary"
            style={{ marginTop: '1rem' }}
          >
            {texts.backToList}
          </button>
        </div>
      </div>
    );
  }

  const statusStyle = getStatusColors(pledge.collateralStatus);

  const handleMarkAsHandoverConfirm = async () => {
    if (!pledge || pledge.collateralStatus !== 'AWAITING' || updatingStatus) return;

    setUpdatingStatus(true);
    setStatusModalLoading(true);
    try {
      const collateralResponse = await updateLoanCollateralStatus(pledge.loanId, 'HANDOVER');
      if (!collateralResponse.success) {
        throw new Error(collateralResponse.message || 'Failed to update collateral status');
      }

      const loanResponse = await updateLoanStatus(pledge.loanId, 'COMPLETED');
      if (!loanResponse.success) {
        throw new Error(loanResponse.message || 'Failed to update loan status');
      }

      setPledge(prev => prev ? ({ ...prev, collateralStatus: 'HANDOVER' }) : prev);
      setLoan(prev => prev ? ({ ...prev, status: 'COMPLETED', collateralStatus: 'HANDOVER' }) : prev);
      setToast({ show: true, message: texts.messages.statusSuccess, type: 'success' });
    } catch (error) {
      console.error('Status update error:', error);
      setToast({ show: true, message: error.message || texts.messages.statusError, type: 'error' });
    } finally {
      setUpdatingStatus(false);
      setStatusModalLoading(false);
      setStatusModalVisible(false);
    }
  };
 
  return (
    <div>
      {toast.show && toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={4000}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <div>
        {/* Header */}
        <div style={{
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button
              onClick={() => navigate('/collateral')}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem' }}
            >
              <ArrowLeft size={20} />
              {texts.back}
            </button>
            <div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: 0 }}>
                {collateralListTexts.pledgeId}: {pledge.id?.substring(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
            <span className={`pledge-lifecycle-badge ${pledge.isActive ? 'is-current' : 'is-previous'}`}>
              {pledge.isActive
                ? (texts.currentBadge || 'CURRENT')
                : (texts.previousBadge || 'PREVIOUS')}
            </span>
            <span style={{
              padding: '0.375rem 0.75rem',
              borderRadius: '9999px',
              fontSize: '0.875rem',
              fontWeight: '500',
              backgroundColor: statusStyle.backgroundColor,
              color: statusStyle.color
            }}>
              {texts.loanCollateralStatus || 'Loan'}: {pledge.collateralStatus}
            </span>
            {pledge.collateralStatus === 'AWAITING' && (
              <button
                onClick={() => setStatusModalVisible(true)}
                disabled={updatingStatus}
                className="btn"
                style={{
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: updatingStatus ? 'not-allowed' : 'pointer',
                  opacity: updatingStatus ? 0.7 : 1
                }}
              >
                {updatingStatus ? (
                  <>
                    <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    {texts.statusModal.updating}
                  </>
                ) : (
                  <>{texts.updateStatus}</>
                )}
              </button>
            )}
            {loan && (
              <button
                onClick={() => navigate(`/loans/${pledge.loanId}`)}
                className="btn btn-primary"
                style={{ marginTop: '0' }}
              >
                {texts.viewLoan || 'View Loan'}
              </button>
            )}
          </div>
        </div>

        {/* Pledge Summary Card */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h2 className="card-title">{texts.summaryTitle}</h2>
          </div>
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              {/* Pledge Date */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#e0f2fe',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Calendar style={{ color: '#0284c7' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.pledgeDate}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {formatDate(pledge.pledgeDate)}
                  </div>
                </div>
              </div>

              {/* Borrower Info */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#dbeafe',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <User style={{ color: '#2563eb' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.borrowerTitle}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {pledge.borrowerName || naLabel}
                  </div>
                </div>
              </div>

              {/* Pledged To */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#fef3c7',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Building2 style={{ color: '#d97706' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.pledgedTo}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {pledge.pledgedTo || naLabel}
                  </div>
                </div>
              </div>

              {/* Pledge Amount */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#d1fae5',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DollarSign style={{ color: '#059669' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.pledgedAmount}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {formatCurrency(pledge.pledgeAmount)}
                  </div>
                </div>
              </div>

              {/* Interest Rate */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                <div style={{
                  padding: '0.5rem',
                  background: '#fed7aa',
                  borderRadius: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TrendingUp style={{ color: '#ea580c' }} size={24} />
                </div>
                <div>
                  <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestRate}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: '600', marginTop: '0.25rem' }}>
                    {pledge.interestRate}% {loanTexts.interestPerMonth}
                  </div>
                </div>
              </div>
            </div>

            {/* Current Interest */}
            {interestSummary && (
              <div style={{
                paddingTop: '1rem',
                marginTop: '1rem',
                borderTop: '1px solid var(--border-color)'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  {texts.currentInterestTitle || 'Current Interest'}
                </h3>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestPeriod || 'Period'}</span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {(() => {
                      const start = interestSummary.periodStart
                        || interestSummary.paymentHistory?.[interestSummary.paymentHistory.length - 1]?.periodStart
                        || pledge.pledgeDate;
                      const end = interestSummary.periodEnd
                        || interestSummary.paymentHistory?.[interestSummary.paymentHistory.length - 1]?.periodEnd
                        || pledge.interestFreezeDate;
                      if (!start && !end) return naLabel;
                      return `${formatShortDate(start)} – ${formatShortDate(end)}`;
                    })()}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestDuration || 'Duration'}</span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                    {interestSummary.numberOfDays} {texts.days || 'days'}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestDue || 'Interest Due'}</span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#dc2626' }}>
                    {formatCurrencyExact(interestSummary.interestDue)}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestPaid || 'Interest Paid'}</span>
                  <span style={{
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    color: Number(interestSummary.interestPaid || 0) > 0 ? '#059669' : 'var(--text-primary)'
                  }}>
                    {formatCurrencyExact(
                      interestSummary.interestPaid
                      ?? (interestSummary.paymentHistory || []).reduce(
                        (sum, p) => sum + Number(p.amountPaid || 0),
                        0
                      )
                    )}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0',
                  borderBottom: '1px solid var(--border-color)'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestOutstanding || 'Outstanding'}</span>
                  <span style={{ fontWeight: '600', fontSize: '0.875rem', color: '#dc2626' }}>
                    {formatCurrencyExact(interestSummary.outstanding)}
                  </span>
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0.75rem 0'
                }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>{texts.interestStatus || 'Status'}</span>
                  <span style={{
                    fontWeight: '600',
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.375rem',
                    backgroundColor: interestSummary.status === 'PAID' ? '#d1fae5' : '#fee2e2',
                    color: interestSummary.status === 'PAID' ? '#166534' : '#991b1b'
                  }}>
                    {interestSummary.status === 'PAID'
                      ? (texts.interestStatusPaid || 'PAID')
                      : (texts.interestStatusUnpaid || 'UNPAID')}
                  </span>
                </div>

                {interestSummary.canPay && Number(interestSummary.outstanding) > 0 && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: '0.75rem' }}
                    onClick={() => setInterestPayModalVisible(true)}
                  >
                    {(texts.payInterest || 'Pay Interest')} {formatCurrencyExact(interestSummary.outstanding)}
                  </button>
                )}
              </div>
            )}

            {/* Interest Payment History */}
            {interestSummary?.paymentHistory?.length > 0 && (
              <div style={{
                paddingTop: '1rem',
                marginTop: '1rem',
                borderTop: '1px solid var(--border-color)'
              }}>
                <h3 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem', color: 'var(--text-primary)' }}>
                  {texts.interestHistoryTitle || 'Interest Payment History'}
                </h3>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--border-color)', textAlign: 'left', color: 'var(--text-secondary)' }}>
                        <th style={{ padding: '0.5rem 0.25rem', fontWeight: 600 }}>{texts.interestHistoryDate || 'Date'}</th>
                        <th style={{ padding: '0.5rem 0.25rem', fontWeight: 600 }}>{texts.interestHistoryPeriod || 'Period'}</th>
                        <th style={{ padding: '0.5rem 0.25rem', fontWeight: 600 }}>{texts.interestHistoryDays || 'Days'}</th>
                        <th style={{ padding: '0.5rem 0.25rem', fontWeight: 600 }}>{texts.interestHistoryAmount || 'Amount'}</th>
                        <th style={{ padding: '0.5rem 0.25rem', fontWeight: 600 }}>{texts.interestHistoryStatus || 'Status'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interestSummary.paymentHistory.map((payment) => (
                        <tr key={payment.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '0.75rem 0.25rem' }}>{formatShortDate(payment.paymentDate)}</td>
                          <td style={{ padding: '0.75rem 0.25rem' }}>
                            {formatShortDate(payment.periodStart)} – {formatShortDate(payment.periodEnd)}
                          </td>
                          <td style={{ padding: '0.75rem 0.25rem' }}>{payment.numberOfDays}</td>
                          <td style={{ padding: '0.75rem 0.25rem', fontWeight: 600 }}>
                            {formatCurrencyExact(payment.amountPaid)}
                          </td>
                          <td style={{ padding: '0.75rem 0.25rem' }}>
                            <span style={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              padding: '0.2rem 0.45rem',
                              borderRadius: '0.375rem',
                              backgroundColor: '#d1fae5',
                              color: '#166534'
                            }}>
                              {payment.paymentStatus || 'PAID'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {loanPledges.length > 0 && (
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-header">
              <h2 className="card-title">{texts.pledgeHistoryTitle || 'Pledge History'}</h2>
            </div>
            <ul className="pledge-history-timeline pledge-history-timeline-inline">
              {loanPledges.map((item, index) => {
                const isCurrent = Boolean(item.isActive);
                const isSelected = item.id === pledge.id;
                const label = isCurrent
                  ? (texts.currentBadge || 'CURRENT')
                  : (index === 1
                    ? (texts.previousBadge || 'PREVIOUS')
                    : (texts.olderBadge || 'OLDER'));
                const endLabel = isCurrent
                  ? (texts.presentLabel || 'Present')
                  : formatShortDate(item.interestFreezeDate || item.updatedAt);
                return (
                  <li
                    key={item.id}
                    className={`pledge-history-item ${isCurrent ? 'is-current' : 'is-previous'}${isSelected ? ' is-selected' : ''}`}
                    onClick={() => {
                      if (item.id !== pledge.id) {
                        navigate(`/pledges/${item.id}`, { state: { pledge: item } });
                      }
                    }}
                    style={{ cursor: item.id === pledge.id ? 'default' : 'pointer' }}
                  >
                    <span className="pledge-history-dot" />
                    <div className="pledge-history-card">
                      <div className="pledge-history-card-top">
                        <strong>{item.pledgedTo || naLabel}</strong>
                        <span className={`pledge-history-badge ${isCurrent ? 'current' : 'previous'}`}>
                          {label}
                        </span>
                      </div>
                      <div className="pledge-history-meta">
                        {formatCurrency(item.pledgeAmount)} · {item.interestRate}%
                      </div>
                      <div className="pledge-history-dates">
                        {formatShortDate(item.pledgeDate)} → {endLabel}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Two Column Layout */}
        <div className="detail-layout-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          {/* Left Column - Associated Loan */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Loan Information */}
            {loan && (
              <div className="card">
                <div className="card-header">
                  <h2 className="card-title">{texts.associatedLoan || 'Associated Loan'}</h2>
                </div>
                <div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.loanId || 'Loan ID'}</span>
                      <span style={{ fontWeight: '600', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                        {loan.loanNumber || loan.id}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{loanTexts.serialNumber || 'Serial Number'}</span>
                      <span style={{
                        fontWeight: 700,
                        fontSize: '0.875rem',
                        color: (pledge?.serialNumber || loan.serialNumber) ? '#1d4ed8' : '#9ca3af',
                        fontStyle: (pledge?.serialNumber || loan.serialNumber) ? 'normal' : 'italic',
                        textTransform: (pledge?.serialNumber || loan.serialNumber) ? 'uppercase' : 'none'
                      }}>
                        {pledge?.serialNumber || loan.serialNumber || t('loans.labels.serialMissing') || '-'}
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.loanAmount || 'Loan Amount'}</span>
                      <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                        {formatCurrency(loan.totalAdvanced ?? loan.finalDisbursedAmount)}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.loanStatus || 'Loan Status'}</span>
                      <span style={{
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        ...getStatusColors(loan.status || 'AWAITED')
                      }}>{loan.status || 'AWAITED'}</span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.metalType || 'Metal Type'}</span>
                      <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                        {loan.metalType || 'N/A'}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid var(--border-color)'
                    }}>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>{texts.itemDescription || 'Item Description'}</span>
                      <span style={{ fontWeight: '600', fontSize: '0.875rem' }}>
                        {loan.itemDescription || loan.name || 'N/A'}
                      </span>
                    </div>
 
                    <button
                      onClick={() => navigate(`/loans/${pledge.loanId}`)}
                      className="btn btn-primary"
                      style={{ width: '100%', marginTop: '0.5rem' }}
                    >
                      {texts.viewFullLoanDetails || 'View Full Loan Details'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Document */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Pledge Document */}
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">{texts.pledgeDocument || 'Pledge Document'}</h2>
              </div>
              <div style={{ padding: '1rem 1.5rem' }}>
                {pledge.documentUrl && pledge.documentUrl !== 'string' ? (
                  <div style={{ position: 'relative' }}>
                    <img
                      src={pledge.documentUrl}
                      alt="Pledge Document"
                      style={{
                        width: '100%',
                        height: '22rem',
                        objectFit: 'cover',
                        borderRadius: '0.5rem',
                        border: '1px solid var(--border-color)'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    {/* Maximize Button */}
                    <button
                      onClick={() => setShowImageModal(true)}
                      style={{
                        position: 'absolute',
                        top: '0.75rem',
                        right: '0.75rem',
                        background: 'rgba(0, 0, 0, 0.6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(0, 0, 0, 0.8)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(0, 0, 0, 0.6)';
                      }}
                    >
                      <Maximize2 size={20} />
                    </button>
                  </div>
                ) : (
                  <div style={{
                    width: '100%',
                    height: '22rem',
                    background: 'var(--bg-muted)',
                    borderRadius: '0.5rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid var(--border-color)',
                    gap: '0.5rem'
                  }}>
                    <FileText size={48} style={{ color: 'var(--text-secondary)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No document available</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {showImageModal && pledge.documentUrl && pledge.documentUrl !== 'string' && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.9)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '2rem'
            }}
            onClick={() => setShowImageModal(false)}
          >
            <div style={{ position: 'relative', maxWidth: '90%', maxHeight: '90%' }}>
              <img
                src={pledge.documentUrl}
                alt="Pledge Document - Full View"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '0.5rem'
                }}
              />
              <button
                onClick={() => setShowImageModal(false)}
                style={{
                  position: 'absolute',
                  top: '-2.5rem',
                  right: '-2.5rem',
                  background: 'var(--bg-surface)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '2.5rem',
                  height: '2.5rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Status confirmation modal */}
        {statusModalVisible && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'var(--bg-surface)',
              padding: '2rem',
              borderRadius: '12px',
              minWidth: '0',
              width: 'min(360px, 95vw)',
              maxWidth: '90vw',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>{texts.statusModal.title}</h3>
              <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {texts.statusModal.message}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => !statusModalLoading && setStatusModalVisible(false)}
                  style={{ padding: '0.5rem 1rem' }}
                  disabled={statusModalLoading}
                >
                  {texts.statusModal.cancel}
                </button>
                <button
                  className="btn"
                  onClick={handleMarkAsHandoverConfirm}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#10b981',
                    color: 'white',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  disabled={statusModalLoading}
                >
                  {statusModalLoading ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      {texts.statusModal.updating}
                    </>
                  ) : (
                    texts.statusModal.confirm
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Interest payment confirmation modal */}
        {interestPayModalVisible && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: 'var(--bg-surface)',
              padding: '2rem',
              borderRadius: '12px',
              minWidth: '0',
              width: 'min(360px, 95vw)',
              maxWidth: '90vw',
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
            }}>
              <h3 style={{ margin: '0 0 1rem 0', color: 'var(--text-primary)' }}>
                {texts.interestPayModal?.title || 'Pay pledge interest'}
              </h3>
              <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                {(texts.interestPayModal?.message || 'Confirm payment of {amount} for the current interest period.')
                  .replace('{amount}', formatCurrencyExact(interestSummary?.outstanding))}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-outline"
                  onClick={() => !interestPayLoading && setInterestPayModalVisible(false)}
                  style={{ padding: '0.5rem 1rem' }}
                  disabled={interestPayLoading}
                >
                  {texts.interestPayModal?.cancel || texts.statusModal.cancel}
                </button>
                <button
                  className="btn"
                  onClick={handlePayInterestConfirm}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#2563eb',
                    color: 'white',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                  disabled={interestPayLoading}
                >
                  {interestPayLoading ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      {texts.interestPayModal?.paying || 'Paying...'}
                    </>
                  ) : (
                    texts.interestPayModal?.confirm || 'Pay Interest'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PledgeDetails;
